import { parse } from 'qs';
import URL from 'url-parse';

import { isDefaultSort } from '../utils/sorting';
import { SEO_MODE_KEYS_TO_IGNORE } from '../utils/seo-mode';

const FILTERS = {
  'type[]': {
    includeAs: 'path',
    seoKey: 'for-rent',
    value: 'type'
  },
  'topology[]': {
    includeAs: 'path',
    seoKey: 'bedrooms',
    value: 'topology'
  },
  page: {
    includeAs: 'path',
    seoKey: 'page',
    value: 'page'
  }
};

const QUERY_SEPARATOR = '=';
const PATH_SEPARATOR = ':';
const SORT_BY_KEY = 'sortBy';
const PAGE_KEY = 'page';
const IB_FILTER_KEY = 'instantBooking';
const FIRST_PAGE = 1;
const NO_IB_FILTER = 0;
const NO_VERIFIED_FILTER = 0;
const NO_IS_BEST_CHOICE_FILTER = 0;
const VERIFIED_KEY = 'verified';
const IS_BEST_CHOICE_KEY = 'isBestChoice';
const FLEXIBLE_MOVE_IN_KEY = 'flexibleMoveIn';
const FLEXIBLE_MOVE_OUT_KEY = 'flexibleMoveOut';
const FLEXIBLE_DATE_FILTER = 1;
const SEO_MODE = 'seoMode';

const PARAMS_TO_AVOID_CHECKS = {
  [SORT_BY_KEY]: value => isDefaultSort(value),
  [PAGE_KEY]: value => parseInt(value, 10) === FIRST_PAGE,
  [IB_FILTER_KEY]: value => parseInt(value, 10) === NO_IB_FILTER,
  [VERIFIED_KEY]: value => parseInt(value, 10) === NO_VERIFIED_FILTER,
  [IS_BEST_CHOICE_KEY]: value =>
    parseInt(value, 10) === NO_IS_BEST_CHOICE_FILTER,
  [FLEXIBLE_MOVE_IN_KEY]: value => parseInt(value, 10) === FLEXIBLE_DATE_FILTER,
  [FLEXIBLE_MOVE_OUT_KEY]: value =>
    parseInt(value, 10) === FLEXIBLE_DATE_FILTER,
  [SEO_MODE]: () => true
};

const filterNamesforUrl = Object.keys(FILTERS).reduce((acc, key) => {
  const seoKey = FILTERS[key].seoKey || key;
  return {
    ...acc,
    [seoKey]: key
  };
}, {});

const translatedFilters = Object.keys(FILTERS).reduce((acc, filterType) => {
  const { seoKey, value } = FILTERS[filterType];
  return {
    ...acc,
    [value]: seoKey
  };
}, {});

function getFilterUrlName(filterKey) {
  return filterNamesforUrl[filterKey];
}

const generateUrlSliceForFilter = (filterKey, filterValue, separator) => {
  const filterValueArr =
    filterValue instanceof Array ? filterValue : [filterValue];

  return filterValueArr
    .filter(Boolean)
    .sort()
    .map(value => `${filterKey}${separator}${value}`);
};

const filterIsArray = filterName => filterName.includes('[]');

const mapFilterNames = filterPath => {
  const [pathKey, filterValue] = filterPath.split(':');
  return {
    filterName: getFilterUrlName(pathKey),
    filterValue
  };
};

const hasFilter = ({ filterName, filterValue }) => filterName && filterValue;

const mergeFilters = (filterName, filterValue, prevFilter = []) => ({
  [filterName]: filterIsArray(filterName)
    ? [...prevFilter, filterValue]
    : filterValue
});

const getFiltersFromPath = pathname =>
  pathname
    .split('/')
    .map(mapFilterNames)
    .filter(hasFilter)
    .reduce(
      (acc, { filterName, filterValue }) => ({
        ...acc,
        ...mergeFilters(filterName, filterValue, acc[filterName])
      }),
      {}
    );

const getFiltersFromQuery = urlQuery =>
  Object.keys(urlQuery).reduce((acc, queryStr) => {
    const queryValue = urlQuery[queryStr];
    const suffix = Array.isArray(queryValue) ? '[]' : '';

    return {
      ...acc,
      [`${queryStr}${suffix}`]: queryValue
    };
  }, {});

const getAreaParams = areaInfo => {
  if (areaInfo && areaInfo.id) {
    return {
      'areaId[]': [`${areaInfo.id}`],
      area: areaInfo.slug
    };
  }
  return {};
};

export function filtersObjectFromUrl(url, areaInfo = {}) {
  const parsedUrl = new URL(url, query => parse(query.replace(/\?/g, '')));
  const filterFromPath = getFiltersFromPath(parsedUrl.pathname);
  const filterFromQuery = getFiltersFromQuery(parsedUrl.query);
  const areaParams = getAreaParams(areaInfo);

  return {
    ...filterFromPath,
    ...filterFromQuery,
    ...areaParams
  };
}

export const filterBelongsToPath = filterKey =>
  typeof translatedFilters[filterKey] !== 'undefined';

export const generatePathForFilter = (filterKey, filterValue) =>
  filterBelongsToPath(filterKey)
    ? generateUrlSliceForFilter(
        translatedFilters[filterKey],
        filterValue,
        PATH_SEPARATOR
      ).join('/')
    : '';

export const generateQueryForFilter = (filterKey, filterValue) =>
  filterBelongsToPath(filterKey)
    ? ''
    : generateUrlSliceForFilter(filterKey, filterValue, QUERY_SEPARATOR).join(
        '&'
      );

// TODO: Change naming to paramHasDefaultValue or similar
export const shouldAvoidParamInTheUrl = (name, value) => {
  const paramURLChecker = PARAMS_TO_AVOID_CHECKS[name];

  return paramURLChecker ? paramURLChecker(value) : false;
};

export const shouldIgnoreSeoKey = key => SEO_MODE_KEYS_TO_IGNORE.includes(key);

export default {
  generatePathForFilter,
  generateQueryForFilter,
  filterBelongsToPath,
  filtersObjectFromUrl,
  shouldAvoidParamInTheUrl,
  shouldIgnoreSeoKey
};
